import React, { useCallback, useEffect, useState } from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

import CheckOutlinedIcon from '@material-ui/icons/CheckOutlined';
import ClearOutlinedIcon from '@material-ui/icons/ClearOutlined';
import RefreshOutlinedIcon from '@material-ui/icons/RefreshOutlined';

import SimpleDialog from '../Dialog/Dialog';
import { SelectOption } from '../Inputs/Select/Select';
import { ButtonPrimary, ButtonDefault } from '../Button/Button';
import DigitacaoService from '../../services/Digitacao';

const useStyles = makeStyles(theme => ({
    carregando: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: theme.spacing(4),
    },
    carregandoTexto: {
        marginTop: theme.spacing(2),
    },
    erro: {
        padding: theme.spacing(1, 0),
    },
    aviso: {
        display: 'block',
        marginTop: theme.spacing(1),
    },
}));

const rotuloRegiao = ({ regiao, tamanhoFila }) =>
    tamanhoFila > 0 ? `${regiao} (${tamanhoFila})` : `${regiao} (sem provas)`;

const DialogSelecionarRegiao = ({ open, obrigatorio, regiaoAtual, onConfirmar, onCancelar }) => {
    const classes = useStyles();

    const [regioes, setRegioes] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(null);
    const [selecionada, setSelecionada] = useState('');

    const carregarRegioes = useCallback(async () => {
        setCarregando(true);
        setErro(null);

        try {
            const resposta = await DigitacaoService.regioes();

            setRegioes(Array.isArray(resposta.data) ? resposta.data : []);
        } catch (error) {
            setErro('Não foi possível carregar as regiões. Verifique sua conexão e tente novamente.');
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => {
        if (!open) return;

        setSelecionada(regiaoAtual || '');
        carregarRegioes();
    }, [open, regiaoAtual, carregarRegioes]);

    const confirmar = () => {
        if (!selecionada || !onConfirmar) return;

        onConfirmar(selecionada);
    };

    const options = regioes.map(regiao => ({ value: regiao.regiao, label: rotuloRegiao(regiao) }));
    const semProvas = regioes.length > 0 && !regioes.some(regiao => regiao.tamanhoFila > 0);

    const actions = (
        <>
            <ButtonPrimary
                name={obrigatorio ? 'Iniciar Digitação' : 'Confirmar'}
                startIcon={<CheckOutlinedIcon />}
                onClick={confirmar}
                disabled={carregando || Boolean(erro) || !selecionada}
                style={{ margin: 4 }}
            />
            {!obrigatorio &&
                <ButtonDefault
                    name="Cancelar"
                    startIcon={<ClearOutlinedIcon />}
                    onClick={onCancelar}
                    style={{ margin: 4 }}
                />
            }
        </>
    );

    return (
        <SimpleDialog
            openDialog={Boolean(open)}
            title="Selecione a Região"
            closeDialog={obrigatorio ? undefined : onCancelar}
            actions={actions}
            fullWidth={true}
            maxWidth="xs"
        >
            {carregando &&
                <div className={classes.carregando}>
                    <CircularProgress />
                    <Typography variant="body2" color="textSecondary" className={classes.carregandoTexto}>
                        Carregando regiões...
                    </Typography>
                </div>
            }

            {!carregando && erro &&
                <div className={classes.erro}>
                    <Typography variant="body2" color="error">{erro}</Typography>
                    <ButtonDefault
                        name="Tentar novamente"
                        startIcon={<RefreshOutlinedIcon />}
                        onClick={carregarRegioes}
                        style={{ marginTop: 8 }}
                    />
                </div>
            }

            {!carregando && !erro &&
                <>
                    <Typography variant="body2" color="textSecondary">
                        Você não possui vínculo regional. Escolha a região que deseja digitar.
                    </Typography>

                    <SelectOption
                        label="Região"
                        value={selecionada}
                        onChange={event => setSelecionada(event.target.value)}
                        options={options}
                        blankName="Selecione a região"
                    />

                    {semProvas &&
                        <Typography variant="caption" color="textSecondary" className={classes.aviso}>
                            Nenhuma região possui provas na fila no momento.
                        </Typography>
                    }
                </>
            }
        </SimpleDialog>
    );
};

export default DialogSelecionarRegiao;
